import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, FileText, Loader2, AlertCircle } from "lucide-react";
import { useLocation } from "wouter";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";

type ReportType = 'all_pets' | 'interaction_summary';

interface LatestReportResponse {
  reportType: ReportType;
  location: string;
  reportId: string;
  reportDate: string;
}

interface NoReportResponse {
  code: 'NO_REPORT';
  message: string;
}

interface ReportActionItemsProps {
  currentLocation: string;
  defaultReportType: ReportType;
  showOtherType?: boolean;
}

export function ReportActionItems({
  currentLocation,
  defaultReportType,
  showOtherType = false,
}: ReportActionItemsProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { locations } = useActiveLocations();
  const [isLoading, setIsLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedPickerLocation, setSelectedPickerLocation] = useState<string>("");
  const [pickerReportType, setPickerReportType] = useState<ReportType>(defaultReportType);
  const [noReportError, setNoReportError] = useState<{ message: string; location: string; reportType: ReportType } | null>(null);

  const reportTypeLabel = (type: ReportType) => 
    type === 'all_pets' ? 'All Pets' : 'Interaction Summary';

  const otherReportType: ReportType = 
    defaultReportType === 'all_pets' ? 'interaction_summary' : 'all_pets';

  const isSystemWide = currentLocation === 'system' || currentLocation === 'all' || !currentLocation;

  const navigateToReport = async (location: string, reportType: ReportType) => {
    setIsLoading(true);
    setNoReportError(null);

    try {
      const params = new URLSearchParams({
        location: location.toUpperCase(),
        type: reportType,
      });
      
      const response = await fetch(`/api/reports/latest?${params.toString()}`, {
        credentials: 'include',
      });

      if (response.status === 404) {
        const errorData: NoReportResponse = await response.json();
        setNoReportError({
          message: errorData.message,
          location: location.toUpperCase(),
          reportType,
        });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch latest report');
      }

      const data: LatestReportResponse = await response.json();
      
      if (reportType === 'all_pets') {
        setLocation(`/reports/${data.reportId}`);
      } else {
        setLocation(`/reports/behavior/${data.reportId}`);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to navigate to report",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleActionClick = (reportType: ReportType) => {
    if (isSystemWide) {
      setPickerReportType(reportType);
      setShowLocationPicker(true);
    } else {
      navigateToReport(currentLocation, reportType);
    }
  };

  const handlePickerConfirm = () => {
    if (selectedPickerLocation) {
      navigateToReport(selectedPickerLocation, pickerReportType);
      setShowLocationPicker(false);
      setSelectedPickerLocation("");
    }
  };

  const getReportsLinkParams = (location: string, reportType: ReportType) => {
    const params = new URLSearchParams();
    params.set('location', location.toUpperCase());
    if (reportType === 'interaction_summary') {
      params.set('tab', 'interactions');
    }
    return `/reports?${params.toString()}`;
  };

  const locationLabel = isSystemWide 
    ? '' 
    : ` (${locations.find(l => l.code.toUpperCase() === currentLocation.toUpperCase())?.label || currentLocation})`;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            disabled={isLoading}
            data-testid="report-action-items"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">Latest Reports</span>
            <ChevronDown className="h-4 w-4 sm:ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() => handleActionClick(defaultReportType)}
            data-testid="report-action-view-latest"
          >
            <FileText className="h-4 w-4 mr-2" />
            View Latest {reportTypeLabel(defaultReportType)} Report{isSystemWide ? '...' : locationLabel}
          </DropdownMenuItem>
          
          {showOtherType && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleActionClick(otherReportType)}
                data-testid="report-action-view-other-type"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Latest {reportTypeLabel(otherReportType)} Report{isSystemWide ? '...' : locationLabel}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showLocationPicker} onOpenChange={setShowLocationPicker}>
        <DialogContent data-testid="report-action-location-picker">
          <DialogHeader>
            <DialogTitle>Select Location</DialogTitle>
            <DialogDescription>
              Choose a location to view the latest {reportTypeLabel(pickerReportType)} report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select
              value={selectedPickerLocation}
              onValueChange={setSelectedPickerLocation}
            >
              <SelectTrigger data-testid="report-action-location-picker-select">
                <SelectValue placeholder="Select a location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.code} value={loc.code}>
                    {loc.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLocationPicker(false);
                  setSelectedPickerLocation("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePickerConfirm}
                disabled={!selectedPickerLocation || isLoading}
              >
                {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                View Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!noReportError} onOpenChange={() => setNoReportError(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              No Report Available
            </DialogTitle>
            <DialogDescription>
              {noReportError?.message}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Button asChild variant="outline" className="w-full">
              <Link href={noReportError ? getReportsLinkParams(noReportError.location, noReportError.reportType) : '/reports'}>
                Go to Reports to Upload
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
